import { z } from 'zod';
import { iconKey, safeHref } from './blocks.ts';

const text = (max: number) => z.string().trim().max(max);

/** Global brand configuration (step 1 §146): nothing about the agency is hardcoded in components. */
export const brandSchema = z.object({
  brandName: text(80).min(1),
  legalName: text(160).optional(),
  logoUrl: safeHref.optional(),
  faviconUrl: safeHref.optional(),
  defaultOgImageUrl: safeHref.optional(),
  primaryContactEmail: z.string().trim().email().max(254).optional(),
  phone: text(40).optional(),
  address: text(300).optional(),
  socialLinks: z.array(z.object({ network: text(40).min(1), url: z.string().trim().url().max(300).regex(/^https:\/\//, 'Use an https:// link') })).max(8).default([]),
});
export type Brand = z.infer<typeof brandSchema>;

const navLink = z.object({ label: text(40).min(1), href: safeHref });
/**
 * Dropdown links. The optional fields (decision 0004) turn a dropdown into a mega-menu: links with a description become cards,
 * one `featured` link becomes the highlighted card, the rest stay a plain list. Settings stored before them still parse.
 */
const navChild = navLink.extend({ description: text(120).optional(), icon: iconKey.optional(), featured: z.boolean().optional(), badge: text(24).optional() });
export const navigationSchema = z.object({
  items: z.array(z.object({ label: text(40).min(1), href: safeHref, children: z.array(navChild).max(12).default([]) })).max(10),
  primaryCta: navLink,
  secondaryCta: navLink.optional(),
});
export type Navigation = z.infer<typeof navigationSchema>;

export const footerSchema = z.object({
  columns: z.array(z.object({ title: text(40).min(1), links: z.array(navLink).max(12) })).max(5),
  legalLinks: z.array(navLink).max(6),
  note: text(240).optional(),
});
export type Footer = z.infer<typeof footerSchema>;

export const defaultSeoSchema = z.object({ titleSuffix: text(40).optional(), description: text(170).optional(), robots: z.enum(['index', 'noindex']).default('index') });
export type DefaultSeo = z.infer<typeof defaultSeoSchema>;

export const settingSchemas = { brand: brandSchema, navigation: navigationSchema, footer: footerSchema, defaultSeo: defaultSeoSchema } as const;
export type SettingKey = keyof typeof settingSchemas;
export const settingKeys = Object.keys(settingSchemas) as SettingKey[];

export const defaultBrand: Brand = { brandName: 'Agency', socialLinks: [] };
export const defaultNavigation: Navigation = {
  items: [
    { label: 'Services', href: '/services', children: [] },
    { label: 'Solutions', href: '/solutions', children: [] },
    { label: 'Industries', href: '/industries', children: [] },
    { label: 'Case studies', href: '/case-studies', children: [] },
    { label: 'Insights', href: '/insights', children: [] },
    { label: 'Company', href: '/about', children: [{ label: 'About', href: '/about' }, { label: 'Process', href: '/process' }] },
    { label: 'Contact', href: '/contact', children: [] },
  ],
  primaryCta: { label: 'Get an estimate', href: '/contact' },
  secondaryCta: { label: 'View case studies', href: '/case-studies' },
};
export const defaultFooter: Footer = {
  columns: [
    { title: 'Services', links: [{ label: 'Web development', href: '/services/web-development' }, { label: 'Mobile apps', href: '/services/mobile-app-development' }, { label: 'CRM development', href: '/services/crm-development' }] },
    { title: 'Company', links: [{ label: 'About', href: '/about' }, { label: 'Process', href: '/process' }, { label: 'Contact', href: '/contact' }] },
  ],
  legalLinks: [{ label: 'Privacy', href: '/privacy' }, { label: 'Cookies', href: '/cookies' }, { label: 'Terms', href: '/terms' }],
};

// ---------- Locales, markets, services (step 1 §19, §156, §157) ----------
export const localeInputSchema = z.object({
  code: z.string().trim().regex(/^[a-z]{2}(-[A-Z]{2})?$/, 'Use a code like en, de or de-CH'),
  name: text(60).min(1),
  countryCode: z.string().trim().regex(/^[A-Z]{2}$/).optional(),
  hreflang: z.string().trim().regex(/^[a-z]{2}(-[A-Za-z]{2})?$/).optional(),
  urlPrefix: z.string().trim().regex(/^[a-z]{2}(-[a-z]{2})?$/).optional(),
  currency: z.string().trim().regex(/^[A-Z]{3}$/).optional(),
  timezone: text(60).optional(),
  enabled: z.boolean().default(false),
});
export type LocaleInput = z.infer<typeof localeInputSchema>;
export const localeViewSchema = localeInputSchema.extend({ languageCode: z.string(), isDefault: z.boolean(), hreflang: z.string(), urlPrefix: z.string() });
export type LocaleView = z.infer<typeof localeViewSchema>;

export const marketInputSchema = z.object({
  code: z.string().trim().regex(/^[A-Z]{2,3}$/, 'Use a short uppercase code like DE'),
  name: text(80).min(1),
  countries: z.array(z.string().regex(/^[A-Z]{2}$/)).min(1).max(20),
  defaultLocale: z.string().trim().min(2).max(10),
  currency: z.string().trim().regex(/^[A-Z]{3}$/),
  timezone: text(60).min(1),
  enabled: z.boolean().default(false),
  advertisingEnabled: z.boolean().default(false),
});
export type MarketInput = z.infer<typeof marketInputSchema>;
export const serviceInputSchema = z.object({
  key: z.string().trim().regex(/^[a-z][a-z0-9-]{1,39}$/),
  name: text(80).min(1),
  description: text(280).min(1),
  enabled: z.boolean().default(true),
});
export type ServiceInput = z.infer<typeof serviceInputSchema>;

export const glossaryInputSchema = z.object({
  term: text(80).min(1),
  kind: z.enum(['brand', 'product', 'technical', 'phrase']).default('technical'),
  doNotTranslate: z.boolean().default(false),
  translations: z.record(z.string().min(2).max(10), text(120)).default({}),
  notes: text(240).optional(),
});
export type GlossaryInput = z.infer<typeof glossaryInputSchema>;

/** What the public site needs in one cacheable call. */
export const publicSiteSchema = z.object({
  brand: brandSchema,
  navigation: navigationSchema,
  footer: footerSchema,
  defaultSeo: defaultSeoSchema,
  locales: z.array(z.object({ code: z.string(), name: z.string(), hreflang: z.string(), urlPrefix: z.string(), isDefault: z.boolean() })),
  services: z.array(z.object({ key: z.string(), name: z.string(), description: z.string(), href: z.string().nullable() })),
  /** Published sibling URL by locale and default-language path; missing translations use the original URL. */
  localizedPaths: z.record(z.string(), z.record(z.string(), z.string())).optional(),
});
export type PublicSite = z.infer<typeof publicSiteSchema>;
